/**
 * ContractTemplateFormDialog - Dialog para criar/editar modelo de contrato
 * 
 * Formulário completo com nome, descrição, categoria, editor HTML e variáveis
 * 
 * @release v1.0.0 - 2026-02-16
 */

import { useState, useEffect, useCallback } from 'react';
import { Loader2, Hash, FileDown, AlertTriangle, Info } from 'lucide-react';
import { MediaUrlInput } from '@/components/ui/media-url-input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ContractTemplateEditor } from './ContractTemplateEditor';
import { SAMPLE_DATA } from './ContractTemplatePreview';
import { ContractFullPreview } from './ContractFullPreview';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { CONTRACT_CATEGORIES, PREDEFINED_VARIABLES, type ContractTemplate, type ContractTemplateInput } from '@/types/contracts';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { showToast } from '@/lib/toast';

// Logger do módulo
const logger = createLogger('ContractTemplateForm');

// Props do dialog
interface ContractTemplateFormDialogProps {
  /** Se o dialog está aberto */
  open: boolean;
  /** Callback ao fechar */
  onClose: () => void;
  /** Template para edição (null = criação) */
  template?: ContractTemplate | null;
  /** Callback ao salvar */
  onSave: (input: ContractTemplateInput & { id?: string }) => void;
  /** Se está salvando */
  isSaving?: boolean;
}

export function ContractTemplateFormDialog({
  open,
  onClose,
  template,
  onSave,
  isSaving = false,
}: ContractTemplateFormDialogProps) {
  const { currentTenant } = useAuth();

  // Estado do formulário
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('geral');
  const [contentHtml, setContentHtml] = useState('');
  const [activeTab, setActiveTab] = useState('editor');
  /** Caminho da imagem de fundo no Storage */
  const [backgroundImagePath, setBackgroundImagePath] = useState<string | null>(null);
  /** URL para preview da imagem de fundo */
  const [backgroundPreviewUrl, setBackgroundPreviewUrl] = useState<string | null>(null);
  // (isUploading removido - agora usa MediaUrlInput)
  /** Se ativa a paginação automática no PDF */
  const [showPageNumbers, setShowPageNumbers] = useState(false);
  /** Posição da paginação: 'top-right' ou 'bottom-right' */
  const [pageNumberPosition, setPageNumberPosition] = useState('bottom-right');
  /** URL do PDF base com cláusulas fixas (merge com anexo dinâmico) */
  const [basePdfPath, setBasePdfPath] = useState<string | null>(null);
  /** Caminho do PDF de capa (primeira página do documento) */
  const [coverPdfPath, setCoverPdfPath] = useState<string | null>(null);
  /** Posição do PDF base: 'before' (antes do anexo) ou 'after' (depois) */
  const [basePdfPosition, setBasePdfPosition] = useState<string>('before');
  /** Margem superior em mm do plano de fundo (padrão: 33mm) */
  const [bgMarginTopMm, setBgMarginTopMm] = useState(33);
  /** Margem inferior em mm do plano de fundo (padrão: 20mm) */
  const [bgMarginBottomMm, setBgMarginBottomMm] = useState(20);
  /** Offset vertical em mm da borda para posicionamento da paginação (padrão: 12mm) */
  const [paginationYOffsetMm, setPaginationYOffsetMm] = useState(12);
  /**
   * Tipo de origem do modelo:
   *  - 'html'         → editor HTML (fluxo tradicional)
   *  - 'uploaded_pdf' → PDF pronto enviado pelo usuário (sem variáveis, sem wizard de dados)
   * @release v2026-05-XX
   */
  const [sourceType, setSourceType] = useState<'html' | 'uploaded_pdf'>('html');
  /** Caminho no Storage do PDF pronto (apenas quando sourceType = 'uploaded_pdf') */
  const [sourcePdfPath, setSourcePdfPath] = useState<string | null>(null);

  /** Controla loading do botão Gerar PDF Test */
  const [isGeneratingTestPdf, setIsGeneratingTestPdf] = useState(false);

  // Preenche formulário ao abrir para edição
  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description || '');
      setCategory(template.category);
      setContentHtml(template.content_html);
      // allowed_delivery_methods e require_selfie removidos — agora em published_contracts
      setBackgroundImagePath(template.background_image_path || null);
      setShowPageNumbers(template.show_page_numbers ?? false);
      setPageNumberPosition(template.page_number_position || 'bottom-right');
      setBasePdfPath(template.base_pdf_path || null);
      setCoverPdfPath((template as any).cover_pdf_path || null);
      setBasePdfPosition((template as any).base_pdf_position || 'before');
      setBgMarginTopMm(template.bg_margin_top_mm ?? 33);
      setBgMarginBottomMm(template.bg_margin_bottom_mm ?? 20);
      setPaginationYOffsetMm(template.pagination_y_offset_mm ?? 12);
      // v2026-05: tipo de origem + PDF pronto
      setSourceType(((template as any).source_type as 'html' | 'uploaded_pdf') || 'html');
      setSourcePdfPath((template as any).source_pdf_storage_path || null);
    } else {
      setName('');
      setDescription('');
      setCategory('geral');
      setContentHtml('');
      // allowed_delivery_methods e require_selfie removidos — agora em published_contracts
      setBackgroundImagePath(null);
      setBackgroundPreviewUrl(null);
      setShowPageNumbers(false);
      setPageNumberPosition('bottom-right');
      setBasePdfPath(null);
      setCoverPdfPath(null);
      setBasePdfPosition('before');
      setBgMarginTopMm(33);
      setBgMarginBottomMm(20);
      setPaginationYOffsetMm(12);
      // v2026-05: default = editor HTML
      setSourceType('html');
      setSourcePdfPath(null);
    }

    setActiveTab('editor');
  }, [template, open]);

  // Resolve URL de preview quando backgroundImagePath muda
  // Se já é URL completa (galeria/anexos), usa direto; se é path legado, gera signed URL
  useEffect(() => {
    if (!backgroundImagePath) {
      setBackgroundPreviewUrl(null);
      return;
    }
    if (backgroundImagePath.startsWith('http')) {
      // URL pública da galeria de mídia - usa diretamente
      setBackgroundPreviewUrl(backgroundImagePath);
    } else {
      // Legado: caminho relativo do bucket contratos - gera signed URL
      supabase.storage
        .from('contratos')
        .createSignedUrl(backgroundImagePath, 3600)
        .then(({ data }) => {
          if (data?.signedUrl) setBackgroundPreviewUrl(data.signedUrl);
        });
    }
  }, [backgroundImagePath]);

  // (handleBackgroundUpload e handleRemoveBackground removidos - agora usa MediaUrlInput)

  /**
   * Gera um PDF de teste com dados fictícios para validar o layout
   * Chama a Edge Function generate-contract-pdf com test_mode=true
   */
  const handleGenerateTestPdf = useCallback(async () => {
    if (!contentHtml.trim() || !currentTenant?.id) return;

    setIsGeneratingTestPdf(true);
    try {
      // Substitui variáveis {{chave}} pelos dados fictícios
      let processedHtml = contentHtml;
      Object.entries(SAMPLE_DATA).forEach(([key, value]) => {
        const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
        processedHtml = processedHtml.replace(regex, value);
      });

      // Chama Edge Function com test_mode
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        showToast.error('Sessão expirada', 'Faça login novamente.');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-contract-pdf`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            test_mode: true,
            tenant_id: currentTenant.id,
            content_html: processedHtml,
            background_image_path: backgroundImagePath,
            show_page_numbers: showPageNumbers,
            page_number_position: pageNumberPosition,
            base_pdf_path: basePdfPath,
            cover_pdf_path: coverPdfPath,
            base_pdf_position: basePdfPosition,
            bg_margin_top_mm: bgMarginTopMm,
            bg_margin_bottom_mm: bgMarginBottomMm,
            pagination_y_offset_mm: paginationYOffsetMm,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Tratamento específico para erro de limite de tamanho do documento
        if (errorData.error_code === 'DOCUMENT_SIZE_LIMIT') {
          showToast.error(
            'Documento muito grande para gerar PDF',
            `Tamanho atual: ${errorData.html_size_kb} KB | Limite: ${errorData.max_size_kb} KB. Reduza o conteúdo HTML do template (remova imagens inline base64, simplifique tabelas).`
          );
          return;
        }
        throw new Error(errorData.error || `Erro ${response.status}`);
      }

      // Recebe PDF como blob e dispara download
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `template-teste-${name.trim() || 'sem-nome'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showToast.success('PDF de teste gerado!', 'O download foi iniciado automaticamente.');
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Erro desconhecido';
      logger.error('Erro ao gerar PDF de teste', { error: msg });
      showToast.error('Erro ao gerar PDF de teste', msg);
    } finally {
      setIsGeneratingTestPdf(false);
    }
  }, [contentHtml, currentTenant?.id, backgroundImagePath, showPageNumbers, pageNumberPosition, basePdfPath, coverPdfPath, basePdfPosition, name, bgMarginTopMm, bgMarginBottomMm, paginationYOffsetMm]);

  // Handler de submit
  const handleSubmit = () => {
    if (!name.trim()) return;

    // v2026-05: quando o modelo é PDF pronto, o upload NÃO ocorre aqui.
    // O PDF real será enviado dentro do wizard, por contrato/negócio, permitindo
    // que cada negócio use um arquivo diferente a partir do mesmo modelo.
    if (sourceType === 'uploaded_pdf') {
      onSave({
        id: template?.id,
        name: name.trim(),
        description: description.trim() || undefined,
        content_html: '',
        variables: [],
        category,
        // Campos exclusivos do modo HTML são zerados
        background_image_path: null,
        show_page_numbers: false,
        page_number_position: 'bottom-right',
        base_pdf_path: null,
        cover_pdf_path: null,
        base_pdf_position: 'before',
        bg_margin_top_mm: 33,
        bg_margin_bottom_mm: 20,
        pagination_y_offset_mm: 12,
        // Marcadores do novo tipo — path fica sempre null (upload é no wizard)
        source_type: 'uploaded_pdf',
        source_pdf_storage_path: null,
      });
      return;
    }

    // Modo HTML (fluxo tradicional) — extrai variáveis usadas no HTML
    const usedVariableKeys = new Set<string>();
    const regex = /\{\{(\w+)\}\}/g;
    let match;
    while ((match = regex.exec(contentHtml)) !== null) {
      usedVariableKeys.add(match[1]);
    }

    // Mapeia para lista de variáveis com metadados
    const variables = PREDEFINED_VARIABLES.filter((v) =>
      usedVariableKeys.has(v.key)
    );

    onSave({
      id: template?.id,
      name: name.trim(),
      description: description.trim() || undefined,
      content_html: contentHtml,
      variables,
      category,
      background_image_path: backgroundImagePath,
      show_page_numbers: showPageNumbers,
      page_number_position: pageNumberPosition,
      base_pdf_path: basePdfPath,
      cover_pdf_path: coverPdfPath,
      base_pdf_position: basePdfPosition,
      bg_margin_top_mm: bgMarginTopMm,
      bg_margin_bottom_mm: bgMarginBottomMm,
      pagination_y_offset_mm: paginationYOffsetMm,
      // Marcadores do modo HTML
      source_type: 'html',
      source_pdf_storage_path: null,
    });
  };


  const isEditing = !!template;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl h-[90vh] max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header fixo */}
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {isEditing ? 'Editar Modelo de Contrato' : 'Novo Modelo de Contrato'}
          </DialogTitle>
        </DialogHeader>

        {/* Corpo scrollável */}
        <div className="flex-1 min-h-0 overflow-y-auto space-y-4 px-1">
          {/* Campos básicos */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-1.5">
              <Label htmlFor="template-name">Nome do Modelo</Label>
              <Input
                id="template-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Contrato de Prestação de Serviço"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Categoria</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTRACT_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="template-desc">Descrição (opcional)</Label>
            <Textarea
              id="template-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Breve descrição do modelo..."
              rows={2}
            />
          </div>

          {/* Nota: Método de envio e exigir selfie foram movidos para a tela Contratos (published_contracts) */}

          {/*
            v2026-05-XX: seletor do tipo de modelo
            - 'html'          → editor HTML tradicional (variáveis, capa, fundo, PDF base, paginação)
            - 'uploaded_pdf'  → PDF pronto (upload); wizard pula variáveis e o backend
                                cria o contrato já 'generated' apontando para o PDF
          */}
          <div className="space-y-2">
            <Label>Tipo de modelo</Label>
            <RadioGroup
              value={sourceType}
              onValueChange={(v) => setSourceType(v as 'html' | 'uploaded_pdf')}
              className="grid grid-cols-1 md:grid-cols-2 gap-3"
            >
              <label
                htmlFor="source-html"
                className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                  sourceType === 'html' ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent/50'
                }`}
              >
                <RadioGroupItem value="html" id="source-html" className="mt-1" />
                <div className="space-y-1">
                  <p className="text-sm font-medium leading-none">Editor Online</p>
                  <p className="text-xs text-muted-foreground">
                    Escreva o contrato com variáveis dinâmicas, capa, plano de fundo e PDF base.
                  </p>
                </div>
              </label>
              <label
                htmlFor="source-pdf"
                className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                  sourceType === 'uploaded_pdf' ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent/50'
                }`}
              >
                <RadioGroupItem value="uploaded_pdf" id="source-pdf" className="mt-1" />
                <div className="space-y-1">
                  <p className="text-sm font-medium leading-none">PDF pronto (upload)</p>
                  <p className="text-xs text-muted-foreground">
                    Sem conteúdo pré-definido. O PDF será enviado no wizard, ao gerar cada contrato — ideal para contratos personalizados por cliente.
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* ============================================================
              MODO 'uploaded_pdf' — nota informativa (upload ocorre no wizard)
              ============================================================ */}
          {sourceType === 'uploaded_pdf' && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Este modelo não guarda nenhum PDF fixo. Ao clicar em <strong>Gerar novo contrato</strong> em um negócio,
                o wizard exibirá uma etapa para <strong>enviar o PDF específico daquele contrato</strong>.
                Assim é possível utilizar o mesmo modelo para clientes diferentes com arquivos personalizados.
              </AlertDescription>
            </Alert>
          )}

          {/* ============================================================
              MODO 'html' — bloco tradicional (capa, fundo, PDF base, editor)
              ============================================================ */}
          {sourceType === 'html' && (
            <>
              {/* Grid lado a lado: Capa + Plano de Fundo + PDF Base (1/3 cada) */}
              <div className="grid grid-cols-3 gap-4">
                {/* Capa do Contrato */}
                <div className="space-y-1.5">
                  <MediaUrlInput
                    value={coverPdfPath}
                    onChange={(url) => {
                      // Valida que o arquivo selecionado é um PDF
                      if (url && !url.toLowerCase().includes('.pdf')) {
                        showToast.error('Arquivo inválido', 'A capa do contrato deve ser um arquivo PDF (.pdf)');
                        return;
                      }
                      setCoverPdfPath(url);
                    }}
                    label="Capa do Contrato"
                    placeholder="Selecione o PDF da galeria"
                    showPreview={true}
                    allowedTypes={['document']}
                  />
                  <p className="text-xs text-muted-foreground">
                    PDF de capa (1 página). Será a primeira página do documento final.
                    {' '}<span className="font-medium text-destructive">Apenas arquivos .pdf</span>
                  </p>
                </div>

                {/* Plano de Fundo */}
                <div className="space-y-1.5">
                  <MediaUrlInput
                    value={backgroundImagePath}
                    onChange={(url) => {
                      setBackgroundImagePath(url);
                      setBackgroundPreviewUrl(url);
                    }}
                    label="Plano de Fundo (papel timbrado)"
                    placeholder="Selecione da galeria"
                    showPreview={true}
                    allowedTypes={['image']}
                  />
                  <p className="text-xs text-muted-foreground">
                    Imagem de fundo em cada página do PDF.
                  </p>
                </div>

                {/* PDF Base */}
                <div className="space-y-1.5">
                  <MediaUrlInput
                    value={basePdfPath}
                    onChange={(url) => {
                      // Valida que o arquivo selecionado é um PDF
                      if (url && !url.toLowerCase().includes('.pdf')) {
                        showToast.error('Arquivo inválido', 'O PDF Base deve ser um arquivo PDF (.pdf)');
                        return;
                      }
                      setBasePdfPath(url);
                    }}
                    label="PDF Base (cláusulas fixas)"
                    placeholder="Selecione o PDF da galeria"
                    showPreview={true}
                    allowedTypes={['document']}
                  />
                  <p className="text-xs text-muted-foreground">
                    PDF pronto com cláusulas fixas do contrato.
                    {' '}<span className="font-medium text-destructive">Apenas arquivos .pdf</span>
                  </p>
                </div>
              </div>

              {/* Alertas orientativos abaixo do grid - largura completa */}
              {backgroundImagePath && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    A imagem ocupará a página inteira (210×297mm). Mantenha as áreas
                    de <strong>topo (~{bgMarginTopMm}mm)</strong> e <strong>rodapé (~{bgMarginBottomMm}mm)</strong> livres
                    de informações importantes para evitar sobreposição com o conteúdo
                    e a paginação do contrato.
                  </AlertDescription>
                </Alert>
              )}

              {/* Inputs de margem de background — visíveis somente quando há background */}
              {backgroundImagePath && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Margem topo do cabeçalho (mm)</Label>
                    <Input
                      type="number"
                      min={10}
                      max={80}
                      value={bgMarginTopMm}
                      onChange={(e) => setBgMarginTopMm(Number(e.target.value))}
                    />
                    <p className="text-xs text-muted-foreground">
                      Espaço reservado para o cabeçalho do plano de fundo. Padrão: 33mm.
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Margem rodapé (mm)</Label>
                    <Input
                      type="number"
                      min={10}
                      max={80}
                      value={bgMarginBottomMm}
                      onChange={(e) => setBgMarginBottomMm(Number(e.target.value))}
                    />
                    <p className="text-xs text-muted-foreground">
                      Espaço reservado para o rodapé do plano de fundo. Padrão: 20mm.
                    </p>
                  </div>
                </div>
              )}
              {basePdfPath && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    <strong>Modo Híbrido ativo</strong> — O PDF final será composto pelo PDF base (cláusulas) +
                    páginas geradas a partir do editor HTML (anexo). Isso otimiza a performance de geração.
                    <div className="mt-2 flex items-center gap-2">
                      <Label className="text-xs font-medium whitespace-nowrap">Posição do PDF Base:</Label>
                      <Select value={basePdfPosition} onValueChange={setBasePdfPosition}>
                        <SelectTrigger className="h-7 w-[200px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="before">Antes do anexo (padrão)</SelectItem>
                          <SelectItem value="after">Depois do anexo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Controles de paginação do PDF */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="showPageNumbers"
                  checked={showPageNumbers}
                  onCheckedChange={(checked) => setShowPageNumbers(checked === true)}
                />
                <Label htmlFor="showPageNumbers" className="text-sm font-medium cursor-pointer">
                  Exibir número de página
                </Label>
              </div>

              {showPageNumbers && (
                <>
                  <RadioGroup
                    value={pageNumberPosition}
                    onValueChange={setPageNumberPosition}
                    className="flex items-center gap-4 ml-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="bottom-right" id="pos-bottom" />
                      <Label htmlFor="pos-bottom" className="text-sm cursor-pointer">Rodapé direito</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="top-right" id="pos-top" />
                      <Label htmlFor="pos-top" className="text-sm cursor-pointer">Topo direito</Label>
                    </div>
                  </RadioGroup>
                  {/* Input de offset vertical para ajuste fino da posição da paginação */}
                  <div className="flex items-center gap-3 ml-6 mt-2">
                    <Label className="text-xs text-muted-foreground whitespace-nowrap">
                      Distância da borda (mm):
                    </Label>
                    <Input
                      type="number"
                      min={5}
                      max={60}
                      value={paginationYOffsetMm}
                      onChange={(e) => setPaginationYOffsetMm(Number(e.target.value))}
                      className="w-20 h-7 text-sm"
                    />
                    <span className="text-xs text-muted-foreground">
                      mm da {pageNumberPosition === 'top-right' ? 'borda superior' : 'borda inferior'}
                    </span>
                  </div>
                </>
              )}

              {/* Editor com abas Editor/Preview */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 min-h-0">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="editor">Editor</TabsTrigger>
                  <TabsTrigger value="preview">Pré-visualização</TabsTrigger>
                </TabsList>
                <TabsContent value="editor" className="flex-1 mt-2">
                  {/* key dinâmica força re-mount do TipTap ao abrir dialog com template diferente */}
                  <ContractTemplateEditor
                    key={`editor-${template?.id ?? 'new'}`}
                    content={contentHtml}
                    onChange={setContentHtml}
                    backgroundPreviewUrl={backgroundPreviewUrl}
                    showPageNumbers={showPageNumbers}
                    pageNumberPosition={pageNumberPosition}
                  />
                </TabsContent>
                <TabsContent value="preview" className="flex-1 mt-2">
                  {/*
                    v2.0: usa ContractFullPreview para também exibir capa e PDF base
                    (paridade com o PDF gerado). Em desktop a UI permanece idêntica.
                  */}
                  <ContractFullPreview
                    contentHtml={contentHtml}
                    backgroundImagePath={backgroundImagePath}
                    coverPdfPath={coverPdfPath}
                    basePdfPath={basePdfPath}
                    basePdfPosition={basePdfPosition === 'after' ? 'after' : 'before'}
                    showPageNumbers={showPageNumbers}
                    pageNumberPosition={pageNumberPosition}
                    bgMarginTopMm={bgMarginTopMm}
                    bgMarginBottomMm={bgMarginBottomMm}
                    paginationYOffsetMm={paginationYOffsetMm}
                  />
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>


        {/* Footer fixo */}
        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          {/* Botão "Gerar PDF Test" só faz sentido no modo HTML (renderiza o editor) */}
          {sourceType === 'html' && (
            <Button
              variant="outline"
              onClick={handleGenerateTestPdf}
              disabled={isGeneratingTestPdf || !contentHtml.trim()}
            >
              {isGeneratingTestPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              Gerar PDF Test
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            disabled={isSaving || !name.trim()}
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {isEditing ? 'Salvar Alterações' : 'Criar Modelo'}
          </Button>

        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
